// Context sheet before "Remix in Ableton". Two paths:
// 1. Has the extension → "Send to Live" (creates a notification the extension picks up)
// 2. Doesn't have it → redirect to the download page

import { LiveDifferencesNote } from "./LiveDifferencesNote";
import { useEffect, useState } from "react";
import { remixInAbletonUrl } from "@/lib/ctas";
import { webApi, json } from "@web-shared/lib/web-api";
import { track } from "@web-shared/lib/analytics";
import { getSession } from "@web-shared/lib/account";
import { useAuthGate } from "../auth";
import { useSession } from "../session";

export function AbletonInterstitial({
  seedId,
  seedTitle,
  producer,
  trackCount,
  onClose,
}: {
  seedId: string;
  seedTitle: string;
  producer: string;
  trackCount: number;
  onClose: () => void;
}) {
  const session = useSession();
  const gate = useAuthGate();
  const [step, setStep] = useState<"ask" | "sent" | "have-ableton" | "download" | "no-ableton">("ask");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Reads the session itself rather than the render's: after the sign-in
  // dialog this runs from the gate, before the sheet has re-rendered.
  const sendToLive = async () => {
    if (!getSession() || sending) return;
    setSending(true);
    try {
      await webApi(`/seeds/${encodeURIComponent(seedId)}/send-to-live`, json("POST", {}));
      track("sent_to_ableton", { seedId });
      track("remix_started", { seedId, detail: "ableton" });
    } catch {
      // best effort
    }
    setSending(false);
    setStep("sent");
  };

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        <div className="relative mx-4 w-full max-w-sm rounded-2xl border border-white/10 bg-[#141419] p-6 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">

          {step === "ask" && (
            <>
              {/* Seed context */}
              <div className="flex items-center gap-3">
                <div className="size-11 rounded-xl bg-[#0d0d12] border border-white/10 flex items-center justify-center shrink-0">
                  <img src="/ableton-icon.webp" alt="" className="h-4 w-auto invert" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-bold truncate">{seedTitle}</h3>
                  <p className="text-[11px] text-muted-foreground">{trackCount} tracks · {producer}</p>
                </div>
              </div>

              <LiveDifferencesNote seedId={seedId} />
              <p className="mt-4 text-sm font-semibold">Do you have the Seedbed extension?</p>

              <div className="mt-3 flex flex-col gap-2">
                {session ? (
                  <button
                    type="button"
                    onClick={() => void sendToLive()}
                    disabled={sending}
                    className="h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {sending ? "Sending..." : "Yes, send to Live"}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => gate.requireSignIn("send_to_live", "Sign in to send this to Live", () => void sendToLive())}
                    className="h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center"
                  >
                    Sign in to send to Live
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setStep("have-ableton")}
                  className="h-11 rounded-xl border border-white/15 text-sm font-semibold flex items-center justify-center"
                >
                  No, get the extension
                </button>
              </div>

              <button type="button" onClick={onClose} className="mt-3 w-full text-center text-xs text-muted-foreground/50">
                Cancel
              </button>
            </>
          )}

          {step === "sent" && (
            <>
              <div className="flex flex-col items-center text-center gap-3">
                <div className="size-12 rounded-full bg-primary/15 flex items-center justify-center">
                  <svg className="size-6 text-primary" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>
                </div>
                <div>
                  <h3 className="text-base font-bold">Sent to Live</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Open Seedbed in Live. <span className="text-foreground font-medium">{seedTitle}</span> is waiting in your notifications.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="mt-5 h-11 w-full rounded-xl bg-primary text-primary-foreground font-bold text-sm"
              >
                Done
              </button>
            </>
          )}

          {step === "have-ableton" && (
            <>
              <p className="text-sm font-semibold">Do you have Ableton Live 12?</p>
              <p className="mt-1 text-xs text-muted-foreground">The Seedbed extension requires Live 12 (Suite).</p>

              <div className="mt-4 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => setStep("download")}
                  className="h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center"
                >
                  Yes.get the extension
                </button>
                <button
                  type="button"
                  onClick={() => setStep("no-ableton")}
                  className="h-11 rounded-xl border border-white/15 text-sm font-semibold flex items-center justify-center"
                >
                  No
                </button>
              </div>

              <button type="button" onClick={() => setStep("ask")} className="mt-3 w-full text-center text-xs text-muted-foreground">
                ← Back
              </button>
            </>
          )}

          {step === "no-ableton" && (
            <>
              <div className="flex flex-col items-center text-center gap-3">
                <div className="size-11 rounded-xl bg-[#0d0d12] border border-white/10 flex items-center justify-center">
                  <img src="/ableton-icon.webp" alt="" className="h-4 w-auto invert" />
                </div>
                <div>
                  <h3 className="text-base font-bold">Ableton Live 12 required</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    The Seedbed extension only works with Live 12 Suite. You can still remix in Audiotool or the browser editor.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="mt-5 h-11 w-full rounded-xl bg-primary text-primary-foreground font-bold text-sm"
              >
                Got it
              </button>
            </>
          )}

          {step === "download" && (
            <>
              <div className="flex items-center gap-3">
                <div className="size-11 rounded-xl bg-[#0d0d12] border border-white/10 flex items-center justify-center shrink-0">
                  <img src="/ableton-icon.webp" alt="" className="h-4 w-auto invert" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-bold">Get the extension</h3>
                  <p className="text-[11px] text-muted-foreground">Free · Ableton Live 12</p>
                </div>
              </div>

              <p className="mt-3 text-sm text-muted-foreground">
                Install the extension, then <span className="text-foreground font-medium">{seedTitle}</span> lands on its own tracks in your Set.
              </p>

              <a
                href={remixInAbletonUrl(seedId)}
                target="_blank"
                rel="noopener"
                onClick={onClose}
                className="mt-4 h-11 w-full rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center gap-2"
              >
                Go to download page
                <svg className="size-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M7 17L17 7M9 7h8v8" /></svg>
              </a>

              <button type="button" onClick={() => setStep("have-ableton")} className="mt-3 w-full text-center text-xs text-muted-foreground">
                ← Back
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
